import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonComponent, ModalOverlayService, ModalSize, SvgIconComponent } from '@woolworthsnz/styleguide';
import { Subject, distinctUntilChanged, startWith, takeUntil } from 'rxjs';
import { InputComponent } from '@woolworthsnz/form';
import { NgClass, NgIf } from '@angular/common';

export type LogoSize = 'medium' | 'medium-large';

@Component({
	selector: 'wnz-edr',
	template: `
		<div class="edr-container" aria-label="Everyday rewards">
			<cdx-svg-icon [name]="'leaf-logomark'" [size]="!logoSize ? 'medium-large' : logoSize"></cdx-svg-icon>
			<div class="edr-content">
				<h4 *ngIf="title" tabindex="0" [ngClass]="smallerTitleFontSize ? 'smallerTitleFont' : ''">
					{{ title }}
				</h4>
				<ng-container *ngIf="description">
					<p [innerHTML]="description" tabindex="0" [ngClass]="hasAHintIcon ? 'inlineDescription' : ''"></p>
					<button
						*ngIf="hasAHintIcon"
						aria-label="Everyday rewards hint"
						type="button"
						cdxButton
						fillStyle="link"
						(click)="hintIconClick()"
						class="hintIcon"
					>
						<cdx-svg-icon name="hint" size="small" fill="currentColor" end></cdx-svg-icon>
					</button>
				</ng-container>
				<ng-container *ngIf="shouldShowACheckbox">
					<form [formGroup]="edrForm" class="edr-checkbox">
						<form-input formControlName="edrCheckbox" inputType="checkbox">
							<label data-cy="edr-checkbox" label for="edrCheckbox">
								<span i18n="@@edr-edrCheckbox">{{ checkboxLabelText }}</span>
							</label>
						</form-input>
					</form>
				</ng-container>
			</div>
		</div>
	`,
	styleUrls: ['./edr.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [SvgIconComponent, NgIf, NgClass, ButtonComponent, FormsModule, ReactiveFormsModule, InputComponent],
})
export class EdrComponent implements OnInit, OnDestroy {
	@Input() title: string;
	@Input() description: string;
	@Input() hasACheckbox = false;
	@Input() checkboxLabelText: string;
	@Input() hasAHintIcon = false;
	@Input() logoSize?: LogoSize;
	@Input() smallerTitleFontSize = false;
	@Output() checkboxValue = new EventEmitter<boolean>();

	edrForm: FormGroup;
	private destroyed$: Subject<boolean> = new Subject();

	get shouldShowACheckbox(): boolean {
		return this.hasACheckbox && !!this.checkboxLabelText;
	}
	constructor(private formBuilder: FormBuilder, private modalOverlayService: ModalOverlayService) {}

	ngOnInit(): void {
		if (this.shouldShowACheckbox) {
			this.edrForm = this.formBuilder.group({
				edrCheckbox: false,
			});

			this.edrForm.controls['edrCheckbox'].valueChanges
				.pipe(takeUntil(this.destroyed$), startWith(false), distinctUntilChanged<boolean>())
				.subscribe((value: boolean) => this.handleCheckboxValueChange(value));
		}
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	handleCheckboxValueChange(value: any): void {
		this.checkboxValue.emit(value);
	}

	hintIconClick(): void {
		this.modalOverlayService.openGenericModal({
			hasCancelButton: false,
			hasActionButton: false,
			hasMultipleParagraphDesc: true,
			align: 'left',
			description:
				"<p>We'll calculate your points total based on your final invoice, in case there are any changes due to substitutions or out of stock items.</p>" +
				"<p>Everyday Rewards boost points aren't collected on added items, if you choose to edit your order after it's been placed.</p>",
			title: "Why can't I see my points yet?",
			icon: undefined,
			modalSize: ModalSize.Large,
		});
	}
}
